import React, { useEffect } from 'react';
import { SecurityAlert } from '../../types/security';
import { securityService } from '../../services/securityService';

interface AlertBannerProps {
  alert: SecurityAlert;
  guestName?: string;
  guestPhoto?: string;
  hotelName?: string;
  riskFactors?: string[];
  onAcknowledge: () => void;
  onDismiss: () => void;
}

const AlertBanner: React.FC<AlertBannerProps> = ({
  alert,
  guestName,
  guestPhoto,
  hotelName,
  riskFactors = [],
  onAcknowledge,
  onDismiss,
}) => {
  useEffect(() => {
    // Play audible alert for critical alerts
    if (alert.severity === 'critical') {
      playAlertSound();
    }
  }, [alert]);

  const playAlertSound = () => {
    // Create audible alert using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Create a more urgent sound for critical alerts
    oscillator.frequency.value = 1000;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);

    // Play twice for emphasis
    setTimeout(() => {
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();

      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);

      oscillator2.frequency.value = 1200;
      oscillator2.type = 'sine';

      gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator2.start(audioContext.currentTime);
      oscillator2.stop(audioContext.currentTime + 0.3);
    }, 400);
  };

  const handleAcknowledge = async () => {
    try {
      await securityService.acknowledgeAlert(alert.id);
      onAcknowledge();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      window.alert('Failed to acknowledge alert. Please try again.');
    }
  };

  const getSeverityStyles = () => {
    switch (alert.severity) {
      case 'critical':
        return 'bg-red-600 border-red-700';
      case 'high':
        return 'bg-orange-600 border-orange-700';
      case 'medium':
        return 'bg-yellow-600 border-yellow-700';
      default:
        return 'bg-blue-600 border-blue-700';
    }
  };

  return (
    <div className={`fixed top-4 right-4 max-w-md w-full ${getSeverityStyles()} text-white rounded-lg shadow-2xl border-2 z-50 animate-pulse`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-2">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-lg font-bold">SECURITY ALERT</h3>
              <p className="text-sm opacity-90">{alert.severity.toUpperCase()} PRIORITY</p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Guest Information */}
        <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-4">
            {guestPhoto ? (
              <img
                src={guestPhoto}
                alt={guestName}
                className="w-16 h-16 rounded-lg object-cover border-2 border-white"
              />
            ) : (
              <div className="w-16 h-16 bg-white bg-opacity-30 rounded-lg flex items-center justify-center">
                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            
            <div className="flex-1">
              <h4 className="font-bold text-lg">{guestName || 'Unknown Guest'}</h4>
              <p className="text-sm opacity-90">{hotelName || 'Unknown Hotel'}</p>
            </div>
          </div>
        </div>

        {/* Alert Details */}
        <div className="mb-4">
          <h4 className="font-semibold mb-2">{alert.title}</h4>
          <p className="text-sm opacity-90 mb-3">{alert.description}</p>
          
          {/* Risk Factors */}
          {riskFactors.length > 0 && (
            <div className="bg-white bg-opacity-20 rounded p-3">
              <p className="text-xs font-semibold mb-2">RISK FACTORS:</p>
              <ul className="text-sm space-y-1">
                {riskFactors.map((factor, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                    <span>{factor}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleAcknowledge}
            className="flex-1 bg-white text-gray-800 font-semibold py-2 px-4 rounded hover:bg-gray-100 transition-colors"
          >
            Acknowledge
          </button>
          <button
            onClick={onDismiss}
            className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded transition-colors"
          >
            Dismiss
          </button>
        </div>

        {/* Timestamp */}
        <div className="mt-3 text-xs text-center opacity-75">
          {new Date(alert.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default AlertBanner;
